import { gql, useMutation } from '@apollo/client';
import {
  WBAlert,
  WBBox,
  WBButton,
  WBFlex,
  WBForm,
  WBLink,
  WBTextField,
} from '@admiin-com/ds-web';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CreateEntityInput,
  updateEntity as UPDATE_ENTITY,
} from '@admiin-com/ds-graphql';
import { WBLocationCompletion } from '@admiin-com/ds-amplify-web';
import { useOnboardingProcess } from '../../../components/OnboardingContainer/OnboadringContainer';
import { AddressResponse } from '@admiin-com/ds-amplify-web';
import { getInitials } from '../../../helpers/string';

const OnboardingBusinessAddressForm = () => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<{ entity: CreateEntityInput }>();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { nextBusiness, isNotOnboarding, onboardingEntityId } =
    useOnboardingProcess();

  const [isManual, setIsManual] = React.useState(false);
  const [updateEntity, { error: updateError }] = useMutation(
    gql(UPDATE_ENTITY)
  );

  const inputs = useMemo(
    () => ({
      address: {
        label: t('businessAddress', { ns: 'onboarding' }),
        name: 'entity.address.fullAddress' as const,
        type: 'text',
        placeholder: t('businessAddressPlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: isManual
          ? {}
          : { required: t('businessAddressRequired', { ns: 'onboarding' }) },
      },
      manual: {
        address1: {
          label: t('address1', { ns: 'onboarding' }),
          name: 'entity.address.address1' as const,
          type: 'text',
          placeholder: t('address1Placeholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules: isManual
            ? {
                required: t('address1Required', { ns: 'onboarding' }),
              }
            : {},
        },
        address2: {
          label: t('address2', { ns: 'onboarding' }),
          name: 'entity.address.address2' as const,
          type: 'text',
          placeholder: t('address2Placeholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules: {},
        },
        city: {
          label: t('city', { ns: 'onboarding' }),
          name: 'entity.address.city' as const,
          type: 'text',
          placeholder: t('cityPlaceholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules: isManual
            ? {
                required: t('cityRequired', { ns: 'onboarding' }),
              }
            : {},
        },
        state: {
          label: t('state', { ns: 'onboarding' }),
          name: 'entity.address.state' as const,
          type: 'text',
          placeholder: t('statePlaceholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules: isManual
            ? {
                required: t('stateRequired', { ns: 'onboarding' }),
              }
            : {},
        },
        country: {
          label: t('country', { ns: 'onboarding' }),
          name: 'entity.address.country' as const,
          type: 'text',
          placeholder: t('countryPlaceholder', { ns: 'onboarding' }),
          defaultValue: 'AUS',
          rules: isManual
            ? {
                required: t('countryRequired', { ns: 'onboarding' }),
              }
            : {},
        },
        postcode: {
          label: t('postcode', { ns: 'onboarding' }),
          name: 'entity.address.postalCode' as const,
          type: 'text',
          placeholder: t('postcodePlaceholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules: isManual
            ? {
                required: t('postcodeRequired', { ns: 'onboarding' }),
              }
            : {},
        },
      },
    }),
    [t, isManual]
  );

  const address1 = watch('entity.address.address1');
  const address2 = watch('entity.address.address2');
  const city = watch('entity.address.city');
  const state = watch('entity.address.state');
  const country = watch('entity.address.country');
  const postalCode = watch('entity.address.postalCode');

  useEffect(() => {
    if (!isManual) return;
    const full = [address1, address2, city, state, postalCode, country]
      .filter(Boolean)
      .join(', ');
    setValue('entity.address.fullAddress', full);
    setValue('entity.address.stateCode', getInitials(state ?? ''));
  }, [
    address1,
    address2,
    city,
    state,
    postalCode,
    country,
    isManual,
    setValue,
  ]);

  const onSubmit = async (data: { entity: CreateEntityInput }) => {
    setLoading(true);
    try {
      const address = data.entity.address;

      if (!isNotOnboarding && onboardingEntityId) {
        await updateEntity({
          variables: {
            input: {
              address: address,
              id: onboardingEntityId,
            },
          },
        });
        // setOnboardingEntity(updateData?.data.updateEntity);
      }

      setLoading(false);

      nextBusiness();
    } catch (err) {
      console.log('ERROR updating entity: ', err);
      setLoading(false);
    }
  };

  return (
    <WBFlex
      flexDirection="column"
      alignItems="center"
      width={{
        xs: '100%',
        sm: '80%',
        md: '60%',
        lg: '40%',
      }}
    >
      <WBForm onSubmit={handleSubmit(onSubmit)} alignSelf="stretch">
        <>
          {''}
          <Controller
            control={control}
            name={inputs.address.name}
            rules={inputs.address.rules}
            defaultValue={inputs.address.defaultValue}
            render={({ field }) => (
              <WBBox>
                <WBLocationCompletion
                  {...field}
                  label={inputs.address.label}
                  type={inputs.address.type}
                  placeholder={inputs.address.placeholder}
                  error={
                    !!(
                      errors.entity?.address?.fullAddress &&
                      errors.entity?.address?.fullAddress.message
                    )
                  }
                  helperText={
                    ((errors.entity?.address?.fullAddress &&
                      errors.entity?.address?.fullAddress.message) as string) ||
                    ''
                  }
                  onChange={(address: AddressResponse) => {
                    console.log('address: ', address);
                    field.onChange(address.label);
                    setValue('entity.address.addressId', address.id);
                    setValue(
                      'entity.address.address1',
                      [address.addressNumber, address.street]
                        .filter(Boolean)
                        .join(' ')
                    );
                    setValue('entity.address.address2', address.unitNumber);
                    setValue('entity.address.postalCode', address.postalCode);
                    setValue('entity.address.city', address.neighborhood);
                    setValue('entity.address.state', address.region);
                    setValue(
                      'entity.address.stateCode',
                      getInitials(address.region)
                    );
                    setValue('entity.address.country', address.country);
                    setValue('entity.address.countryCode', 'AU');
                  }}
                  onLocationLookupStart={() => setDisabled(true)}
                  onLocationLookupEnd={() => setDisabled(false)}
                  margin="dense"
                />
              </WBBox>
            )}
          />
          {!isManual && (
            <WBLink
              variant="body2"
              mt={2}
              underline="always"
              onClick={() => setIsManual(true)}
            >
              {t('enterAddressManually', { ns: 'onboarding' })}
            </WBLink>
          )}
        </>
        {isManual && (
          <>
            <WBFlex flexDirection={['column', 'row']}>
              <WBBox flex={3} pr={[0, 3]}>
                <Controller
                  control={control}
                  name={inputs.manual.address1.name}
                  rules={inputs.manual.address1.rules}
                  defaultValue={inputs.manual.address1.defaultValue}
                  render={({ field }) => (
                    <WBTextField
                      {...field}
                      label={inputs.manual.address1.label}
                      type="text"
                      placeholder={inputs.manual.address1.placeholder}
                      error={!!errors?.entity?.address?.address1}
                      helperText={
                        errors?.entity?.address?.address1?.message || ''
                      }
                      margin="dense"
                    />
                  )}
                />
              </WBBox>
              <WBBox flex={1}>
                <Controller
                  control={control}
                  name={inputs.manual.address2.name}
                  rules={inputs.manual.address2.rules}
                  defaultValue={inputs.manual.address2.defaultValue}
                  render={({ field }) => (
                    <WBTextField
                      {...field}
                      label={inputs.manual.address2.label}
                      type="text"
                      placeholder={inputs.manual.address2.placeholder}
                      error={!!errors?.entity?.address?.address2}
                      helperText={
                        errors?.entity?.address?.address2?.message || ''
                      }
                      margin="dense"
                    />
                  )}
                />
              </WBBox>
            </WBFlex>

            <WBFlex mt={[0, 4]}>
              <Controller
                control={control}
                name={inputs.manual.city.name}
                rules={inputs.manual.city.rules}
                defaultValue={inputs.manual.city.defaultValue}
                render={({ field }) => (
                  <WBTextField
                    {...field}
                    label={inputs.manual.city.label}
                    type="text"
                    placeholder={inputs.manual.city.placeholder}
                    error={!!errors?.entity?.address?.city}
                    helperText={errors?.entity?.address?.city?.message || ''}
                    margin="dense"
                  />
                )}
              />
            </WBFlex>
            <WBFlex flexDirection={['column', 'row']}>
              <Controller
                control={control}
                name={inputs.manual.state.name}
                rules={inputs.manual.state.rules}
                defaultValue={inputs.manual.state.defaultValue}
                render={({ field }) => (
                  <WBBox flex={1} pr={[0, 3]}>
                    <WBTextField
                      {...field}
                      label={inputs.manual.state.label}
                      type="text"
                      placeholder={inputs.manual.state.placeholder}
                      error={!!errors?.entity?.address?.state}
                      helperText={errors?.entity?.address?.state?.message || ''}
                      margin="dense"
                    />
                  </WBBox>
                )}
              />

              <Controller
                control={control}
                name={inputs.manual.country.name}
                rules={inputs.manual.country.rules}
                defaultValue={inputs.manual.country.defaultValue}
                render={({ field }) => (
                  <WBBox flex={1} pr={[0, 3]}>
                    <WBTextField
                      {...field}
                      aria-readonly
                      value={'AUS'}
                      label={inputs.manual.country.label}
                      type="text"
                      placeholder={inputs.manual.country.placeholder}
                      error={!!errors?.entity?.address?.country}
                      helperText={
                        errors?.entity?.address?.country?.message || ''
                      }
                      margin="dense"
                    />
                  </WBBox>
                )}
              />
              <Controller
                control={control}
                name={inputs.manual.postcode.name}
                rules={inputs.manual.postcode.rules}
                defaultValue={inputs.manual.postcode.defaultValue}
                render={({ field }) => (
                  <WBBox flex={1}>
                    <WBTextField
                      {...field}
                      label={inputs.manual.postcode.label}
                      type="text"
                      placeholder={inputs.manual.postcode.placeholder}
                      error={!!errors?.entity?.address?.postalCode}
                      helperText={
                        errors?.entity?.address?.postalCode?.message || ''
                      }
                      margin="dense"
                    />
                  </WBBox>
                )}
              />
            </WBFlex>
          </>
        )}
        <WBButton
          sx={{
            mt: {
              xs: 6,
              sm: 10,
            },
          }}
          disabled={disabled}
          loading={loading}
        >
          {t('nextTitle', { ns: 'common' })}
        </WBButton>
      </WBForm>
      {updateError?.message && (
        <WBAlert title={updateError.message} severity="error" sx={{ my: 2 }} />
      )}
    </WBFlex>
  );
};

export default OnboardingBusinessAddressForm;
